<template>
  <entity-edit-dialog
      @submit="submit"
      @close="close"
      v-model="visible"
      ref="editDialog"
      :dialog-title="dialogTitle"
      :submitting="submitting">
    <template v-slot:contentCard>
      <v-card-text class="pt-5">
        <entity-dialog-input-row :label="$t('partOrder.orderedParts')">
          <part-quantities-select v-model="partOrder.orderedParts"
                                  @change="partSelectionChanged"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('partOrder.receivers')"
                                 :required="true"
                                 divider-before>
          <div>
            <div class="d-flex flex-row flex-wrap">
              <div class="mb-3 mb-md-0">
                <v-select :items="commonSuppliersSelectItems"
                          v-model="partOrder.supplier"
                          outlined
                          dense
                          :label="$t('supplier.singular')"
                          :no-data-text="suppliersNoDataText"
                          hide-details="auto"
                          class="mr-2"
                          style="max-width: 15em;"
                          :rules="[buildRequiredValidator()]"/>
              </div>

              <div>
                <v-select :items="selectableReceiverEmails"
                          v-model="partOrder.receiverEmail"
                          outlined
                          dense
                          hide-details="auto"
                          :label="$t('partOrder.receiverEmail')"
                          :disabled="!partOrder.supplier"
                          :no-data-text="$t('partOrder.noReceiverEmailsForSupplier')"
                          style="max-width: 15em;"
                          :rules="[buildRequiredValidator()]"/>
              </div>

            </div>
            <v-checkbox v-model="partOrder.ccToSender" :label="$t('partOrder.ccToSender')"/>

            <multiple-emails-input v-model="partOrder.otherSuppliers"
                                   :label="$t('partOrder.otherSuppliers')"
                                   :input-placeholder="$t('partOrder.otherSuppliersEmailPlaceholder')"
                                   style="max-width: 25em"/>
          </div>
        </entity-dialog-input-row>

        <entity-dialog-input-row divider-before>
          <v-btn class="mt-3"
                 small
                 @click="generateOrderMessage">
            {{ $t('partOrder.generateOrderMessage') }}
          </v-btn>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('partOrder.title')" :required="true">
          <v-text-field v-model="partOrder.title"
                        outlined
                        dense
                        hide-details="auto"
                        :rules="[buildMaxLengthValidator(256), buildRequiredValidator()]"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('partOrder.message')" :required="true">
          <v-textarea v-model="partOrder.message"
                      rows="8"
                      outlined
                      dense
                      hide-details="auto"
                      :rules="[buildRequiredValidator()]"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row divider-before>
          <v-checkbox v-model="sendAfterSave"
                      hide-details="auto"
                      :label="$t('partOrder.sendAfterSave')"/>
        </entity-dialog-input-row>

        <part-order-mail-sending-dialog ref="mailSendingDialog"
                                        @done="afterSaveSendDone"/>
      </v-card-text>
    </template>
  </entity-edit-dialog>
</template>

<script>
import EntityDialogInputRow from '@/components/entity-crud/EntyDialogInputRow';
import i18n from '@/i18n';
import EntityEditDialog from '@/components/entity-crud/EntityEditDialog';
import PartOrdersService from '@/services/PartOrdersService';
import { buildRequiredIfValidator, buildRequiredValidator, buildMaxLengthValidator } from '@/utils/ValidatorBuilders';
import PartQuantitiesSelect from '@/components/inputs/PartQuantitiesSelect';
import MultipleEmailsInput from '@/components/inputs/MultipleEmailsInput';
import PartOrderMailSendingDialog from '@/views/partorder/PartOrderMailSendingDialog';

export default {
  name: 'PartOrderDialog',
  components: {
    PartOrderMailSendingDialog,
    MultipleEmailsInput,
    PartQuantitiesSelect,
    EntityEditDialog,
    EntityDialogInputRow,
  },
  data: () => ({
    partOrder: null,
    visible: false,
    submitting: false,
    commonSuppliers: [],
    sendAfterSave: false,
  }),
  props: {},
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    dialogTitle() {
      if (this.partOrder?.id) {
        return i18n.t('partOrder.editDialog.titleEdit');
      } else {
        return i18n.t('partOrder.editDialog.titleAdd');
      }
    },
    commonSuppliersSelectItems() {
      return this.commonSuppliers.map(supplier => {
        return {
          text: this.$options.filters.supplier(supplier),
          value: supplier,
        };
      });
    },
    selectableReceiverEmails() {
      if (!this.partOrder.supplier) {
        return [];
      }
      let emails = [this.partOrder.supplier.email];
      this.partOrder.supplier.contactPersons
          .forEach(contact => emails.push(contact.email));

      emails = emails.filter(email => !!email);

      if (this.partOrder.id
          && this.partOrder.receiverEmail
          && !emails.includes(this.partOrder.receiverEmail)) {
        //this is an edit and originally selected email is no longer on the selectable emails
        emails.push(this.partOrder.receiverEmail);
      }
      return emails;
    },

    suppliersNoDataText() {
      if (this.partOrder.orderedParts.length === 0) {
        return this.$t('partOrder.selectSomeParts');
      }
      return this.$t('partOrder.noCommonSuppliersForParts');
    },

    supplierSelectHintText() {
      if (this.partOrder.orderedParts.length !== 0 && this.commonSuppliers.length === 0) {
        return this.$t('partOrder.noCommonSuppliersForParts');
      }
      return null;
    },
  },
  methods: {
    buildRequiredIfValidator,
    buildRequiredValidator,
    buildMaxLengthValidator,

    partSelectionChanged() {
      const partIds = this.partOrder.orderedParts.map(partQuantity => partQuantity.part.id);
      if (partIds.length === 0) {
        this.setCommonSuppliers([]);
        return;
      }
      PartOrdersService.getCommonSuppliersForPartIds(partIds)
          .then(result => {
            this.setCommonSuppliers(result.data);
          })
          .catch(err => {
            console.log(err);
          });
    },

    setCommonSuppliers(suppliers) {
      this.commonSuppliers = suppliers;
      if (this.partOrder.supplier && !this.commonSuppliers.find(supplier => this.partOrder.supplier?.id === supplier.id)) {
        //part selection changed in a way that previosly selected supplier is not on commonSuppliers anymore
        this.partOrder.supplier = null;
        this.partOrder.receiverEmail = null;
      }
    },


    generateOrderMessage() {
      let template = {
        title: this.$t('partOrder.defaultPartOrderTemplate.title'),
        message: this.$t('partOrder.defaultPartOrderTemplate.message'),
      };

      if (this.partOrder.supplier?.partOrderTemplate.title) {
        template.title = this.partOrder.supplier.partOrderTemplate.title;
      }
      if (this.partOrder.supplier?.partOrderTemplate.message) {
        template.message = this.partOrder.supplier.partOrderTemplate.message;
      }

      const partNameMarker = this.$t('partOrder.partOrderTemplatePartNameMarker');
      let orderRows = this.partOrder.orderedParts.map(this.partQuantityToOrderRow).join('\n');
      this.partOrder.title = template.title;
      this.partOrder.message = template.message.replace(partNameMarker, orderRows);
    },

    partQuantityToOrderRow(partQuantity) {
      let text = '';
      if (partQuantity.part.manufacturerIdentification) {
        text = partQuantity.part.manufacturerIdentification + ' ';
      }
      text += partQuantity.part.name + ' ';
      text += partQuantity.quantity + ' ' + this.$options.filters.unit(partQuantity.part.trackingUnit.unit);
      return text;
    },


    submit() {
      if (this.submitting) {
        return;
      }
      if (!this.$refs.editDialog.$refs.form.validate()) {
        this.$refs.editDialog.showFormHasValidationErrorsMessage(i18n.t('partOrder.requiredInformationMissing'));
        return;
      }
      this.submitting = true;


      PartOrdersService.save(this.partOrder).then((result) => {

        if (this.sendAfterSave) {
          this.partOrder = result.data.partOrder;
          this.commonSuppliers = result.data.commonSuppliers;
          this.$refs.mailSendingDialog.open(this.partOrder.id);
        } else {
          this.$emit('done', result.data.partOrder);
          this.close();
        }
      }).catch(err => {
        console.log(JSON.stringify(err));
      }).finally(() => {
        this.submitting = false;
      });
    },

    afterSaveSendDone() {
      this.$emit('done', this.partOrder);
      this.close();
    },

    close() {
      this.partOrder = null;
      this.visible = false;
      this.submitting = false;
      this.commonSuppliers = [];
    },


    openCreateNewPartOrder() {
      this.partOrder = {
        receiverEmail: '',
        ccToSender: false,
        title: '',
        message: '',
        orderedParts: [],
        otherSuppliers: [],
        supplier: null,
      };
      this.visible = true;
    },

    openEditForPartOrder(partOrderId) {
      PartOrdersService.get(partOrderId)
          .then(result => {
            this.partOrder = result.data.partOrder;
            this.commonSuppliers = result.data.commonSuppliers;
            this.visible = true;
          }).catch(err => {
        console.log(JSON.stringify(err));
      });
    },

  },
};
</script>

<style scoped>

</style>
